import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td } from '../components/ui/bits';
import { useRpc, useSel, queryClient } from '../lib/queries';
import type { EmailVolumeResp } from '../lib/types';
import { supabase } from '../lib/supabase';
import { fmtDateTime } from '../lib/format';
import { fmtDay } from '../lib/format';
import { useIsNarrow } from '../hooks/useIsNarrow';

interface PipelineConfig {
  firm_alerts_live: boolean;
  lifecycle_emails_live: boolean;
  referral_rewards_live: boolean;
  lifecycle_email_flags: Record<string, boolean> | null;
  referral_fraud_threshold: number;
  updated_at: string;
}

const CAMPAIGNS: { key: string; kind: 'cron' | 'event' | 'wheel' | 'sentinel'; what: string }[] = [
  { key: 'failure_apology',       kind: 'sentinel', what: 'Auto-repair notice after 3 failed sessions in 6 hours' },
  { key: 'deadline_urgency',      kind: 'cron',  what: 'Deadline approaching nudge' },
  { key: 'free_credit_exhausted', kind: 'cron',  what: 'Free credits used up' },
  { key: 'gift_waiting',          kind: 'cron',  what: 'Welcome gift still waiting' },
  { key: 'abandoned_onboarding',  kind: 'cron',  what: 'Signed up, never finished onboarding' },
  { key: 'social_proof',          kind: 'cron',  what: 'Social proof nudge' },
  { key: 'dunning',               kind: 'event', what: 'Payment failed (transactional)' },
  { key: 'post_purchase',         kind: 'event', what: 'Post-purchase receipt' },
  { key: 'winback',               kind: 'event', what: 'Cancelled winback' },
  { key: 'gift_granted',          kind: 'event', what: 'Welcome gift granted' },
  { key: 'reset_reengagement',    kind: 'event', what: 'Reset re-engagement drip' },
  { key: 'pack_purchase',         kind: 'event', what: 'Pack purchase receipt' },
  { key: 'wheel_claim_reminder',  kind: 'wheel', what: 'Prize wheel claim reminder' },
  { key: 'wheel_trial_reminder',  kind: 'wheel', what: 'Prize wheel trial reminder' },
];

export const EmailPage: React.FC = () => {
  const { data: cfgRows, error, isLoading } = useSel<PipelineConfig[]>(['pipeline-config'], () =>
    supabase.from('pipeline_config').select('*').eq('id', 1));
  const cfg = cfgRows?.[0];
  const { data: volume } = useRpc<EmailVolumeResp>('admin_dash_email_volume', { p_days: 30 });
  const { data: lifecycleSends } = useSel<any[]>(['lifecycle-sends'], () =>
    supabase.from('lifecycle_email_sends').select('user_id, campaign, sent_at, external_ref').order('sent_at', { ascending: false }).limit(40));
  const { data: unsubs } = useSel<any[]>(['unsub-feedback'], () =>
    supabase.from('unsubscribe_feedback').select('reason, comment, created_at').order('created_at', { ascending: false }).limit(10));

  const [toggle, setToggle] = useState<{ kind: 'global' | 'campaign'; key: string; value: boolean } | null>(null);
  const [cohorts, setCohorts] = useState<Record<string, string>>({});
  const [busyCohort, setBusyCohort] = useState<string | null>(null);

  const chartData = volume?.daily ?? [];
  const narrow = useIsNarrow();

  const previewCohort = async (campaign: string) => {
    setBusyCohort(campaign);
    try {
      const { data, error: e } = await supabase.rpc('admin_lifecycle_cohort_preview', { p_campaign: campaign, p_limit: 500 });
      if (e) throw new Error(e.message);
      setCohorts(prev => ({ ...prev, [campaign]: `${(data as any)?.count ?? 0} would send next sweep` }));
    } catch (e) {
      setCohorts(prev => ({ ...prev, [campaign]: (e as Error).message }));
    } finally { setBusyCohort(null); }
  };

  if (isLoading) return <LoadingRow label="Reading the switchboard" />;
  if (error) return <ErrorPanel error={error} label="pipeline_config" />;
  if (!cfg) return <ErrorPanel error="pipeline_config row 1 missing" />;

  const flags = cfg.lifecycle_email_flags ?? {};
  const effective = (key: string) => cfg.lifecycle_emails_live && flags[key] !== false;
  const today = volume?.today;
  const sendsToday = today ? today.lifecycle + today.alerts + today.welcome : 0;

  const GlobalToggle: React.FC<{ label: string; k: 'firm_alerts_live' | 'lifecycle_emails_live' | 'referral_rewards_live'; note: string }> = ({ label, k, note }) => (
    <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-2 p-3 rounded-md border border-stone-100">
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-stone-900">{label}</p>
        <p className="text-[11.5px] text-stone-400">{note}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge tone={cfg[k] ? 'emerald' : 'stone'}>{cfg[k] ? 'On' : 'Off'}</Badge>
        <Button size="sm" variant={cfg[k] ? 'danger' : 'primary'} onClick={() => setToggle({ kind: 'global', key: k, value: !cfg[k] })}>
          Turn {cfg[k] ? 'off' : 'on'}
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Email" subtitle="Every system that can send, with its real effective state. Writes are explicit-field updates of the one config row; nothing here can insert a defaults row." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile label="Sent today" value={sendsToday.toLocaleString('en-GB')}
          sub={today ? `${today.lifecycle} lifecycle, ${today.alerts} alerts, ${today.welcome} welcome. Busiest day in 30: ${volume?.peak_day ? `${volume.peak_day.n.toLocaleString('en-GB')} on ${fmtDay(volume.peak_day.day)}` : 'none'}` : undefined}
          source="Since London midnight. No daily cap: Resend is on a paid plan" />
        <StatTile label="Firm alerts" value={cfg.firm_alerts_live ? 'Armed' : 'Paused'} accent={cfg.firm_alerts_live ? 'emerald' : 'stone'} />
        <StatTile label="Lifecycle" value={cfg.lifecycle_emails_live ? 'On' : 'Off'} sub="cron every 15 minutes" accent={cfg.lifecycle_emails_live ? 'emerald' : 'stone'} />
        <StatTile label="Referral rewards" value={cfg.referral_rewards_live ? 'On' : 'Off'} accent="stone" />
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Section title="Master switches" sub={`Last changed ${fmtDateTime(cfg.updated_at)}.`}>
          <div className="space-y-2">
            <GlobalToggle label="Firm alerts" k="firm_alerts_live" note="Gates all three alert variants (paid, weekly pick, soft intro). Off suppresses new events rather than queueing them." />
            <GlobalToggle label="Lifecycle emails" k="lifecycle_emails_live" note="The global gate above every campaign below. A campaign missing from the flags defaults to live while this is on." />
            <GlobalToggle label="Referral rewards" k="referral_rewards_live" note="The payout sweep for referral rewards." />
          </div>
        </Section>

        <Section title="Emails sent per day, 30 days" sub="Lifecycle, firm-alert and welcome emails, by London day. Pack-claim and sign-in emails are not logged." source="lifecycle_email_sends, alert_sends, profiles.welcome_email_sent_at">
          {chartData.length === 0 ? <EmptyNote>No sends in the window</EmptyNote> : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -22 }}>
                  <CartesianGrid vertical={false} stroke="#F4F4F5" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#71717A' }} tickFormatter={fmtDay} tickLine={false} axisLine={{ stroke: '#E7E7EA' }} interval={narrow ? 7 : 4} />
                  <YAxis tick={{ fontSize: 11, fill: '#71717A' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #E7E7EA' }} labelFormatter={(d: string) => fmtDay(d)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: string) => v === 'lifecycle' ? 'Lifecycle' : v === 'alerts' ? 'Firm alerts' : 'Welcome'} />
                  <Bar dataKey="lifecycle" stackId="a" fill="#5856D6" />
                  <Bar dataKey="alerts" stackId="a" fill="#ABAAEE" />
                  <Bar dataKey="welcome" stackId="a" fill="#0EA5A0" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>

      <Section
        title="Campaigns"
        sub="Effective state = global switch and the campaign flag. A campaign not named in the flags runs live whenever the global is on; that default is why every row is listed here explicitly."
        className="mb-3"
      >
        <TableWrap>
          <thead><tr><Th>Campaign</Th><Th>Kind</Th><Th>Flag</Th><Th>Effective</Th><Th>Cohort</Th><Th></Th></tr></thead>
          <tbody>
            {CAMPAIGNS.map(c => (
              <tr key={c.key}>
                <Td>
                  <span className="font-medium text-stone-900">{c.key}</span>
                  <span className="block text-[11px] text-stone-400">{c.what}</span>
                </Td>
                <Td>{c.kind === 'cron' ? 'Cron sweep' : c.kind === 'wheel' ? 'Wheel' : c.kind === 'sentinel' ? 'Sentinel, every 10 min' : 'Event-driven'}</Td>
                <Td>{flags[c.key] === undefined ? <span className="text-stone-400">not set (defaults live)</span> : flags[c.key] ? 'on' : 'off'}</Td>
                <Td><Badge tone={effective(c.key) ? 'emerald' : 'stone'}>{effective(c.key) ? 'Live' : 'Off'}</Badge></Td>
                <Td>
                  {(c.kind === 'cron' || c.kind === 'wheel') ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <Button size="sm" busy={busyCohort === c.key} onClick={() => previewCohort(c.key)}>Dry run</Button>
                      {cohorts[c.key] && <span className="text-[11px] text-stone-500">{cohorts[c.key]}</span>}
                    </span>
                  ) : <span className="text-[11px] text-stone-400">{c.kind === 'sentinel' ? 'fires on failure streaks, repairs always run' : 'fires on its event'}</span>}
                </Td>
                <Td>
                  <Button size="sm" variant={flags[c.key] === false ? 'primary' : 'danger'}
                    onClick={() => setToggle({ kind: 'campaign', key: c.key, value: flags[c.key] === false })}>
                    {flags[c.key] === false ? 'Enable' : 'Disable'}
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Section>

      <div className="grid md:grid-cols-2 gap-3">
        <Section title="Recent lifecycle sends">
          {(lifecycleSends ?? []).length === 0 ? <EmptyNote>None yet</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Campaign</Th><Th>User</Th><Th>Sent</Th></tr></thead>
              <tbody>
                {(lifecycleSends ?? []).map((s, i) => (
                  <tr key={i}>
                    <Td className="font-medium text-stone-900">{s.campaign}</Td>
                    <Td><UserLink id={s.user_id} /></Td>
                    <Td className="whitespace-nowrap">{fmtDateTime(s.sent_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
        <Section title="Unsubscribe feedback" sub="Exit-survey answers from the token flow. Also: alerts and lifecycle share one sending domain; split before any volume push.">
          {(unsubs ?? []).length === 0 ? <EmptyNote>No feedback yet</EmptyNote> : (
            <div className="space-y-2">
              {(unsubs ?? []).map((u, i) => (
                <div key={i} className="text-[12px]">
                  <span className="font-semibold text-stone-800">{u.reason}</span>
                  {u.comment && <span className="text-stone-500"> · {u.comment}</span>}
                  <span className="text-stone-400"> · {fmtDateTime(u.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <ConfirmDialog
        open={!!toggle} onClose={() => setToggle(null)}
        title={toggle?.value ? `Turn on ${toggle.key}` : `Turn off ${toggle?.key}`}
        tone={toggle?.value ? 'danger' : 'primary'}
        confirmLabel={toggle?.value ? 'Turn on' : 'Turn off'}
        body={
          toggle?.kind === 'global' && toggle.key === 'lifecycle_emails_live' && toggle.value
            ? <>This arms every campaign whose flag is not explicitly off, across roughly a thousand users on the next 15-minute sweep. Check the dry-run counts first.</>
            : toggle?.kind === 'global' && toggle.key === 'firm_alerts_live' && toggle.value
            ? <>Real alert emails start going out the moment the drain sweep next runs against the queue.</>
            : <>Explicit-field update on config row 1. Takes effect on the next sweep.</>
        }
        onConfirm={async () => {
          if (!toggle) return;
          const rpcName = toggle.kind === 'global' ? 'admin_set_email_flag' : 'admin_set_campaign_flag';
          const args = toggle.kind === 'global'
            ? { p_flag: toggle.key, p_value: toggle.value }
            : { p_campaign: toggle.key, p_value: toggle.value };
          const { error: e } = await supabase.rpc(rpcName, args);
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries({ queryKey: ['pipeline-config'] });
          queryClient.invalidateQueries({ queryKey: ['admin_dash_overview'] });
        }}
      />
    </div>
  );
};
